import { useTranslation } from 'react-i18next';
import '../supplier-info.scss';
import CheckboxAtom from '../../../../components/atoms/checkbox/checkbox';
import TextFieldAtom from '../../../../components/atoms/text-field/text-field';

const PdfSectionModal = ({
    selectedSections,
    onSectionToggle,
    onSelectAllSections,
    ticketSections,
    selectedLanguage,
    onLanguageChange,
    invoiceOverviewFilter,
    onInvoiceOverviewFilterChange,
    includeComments,
    onIncludeCommentsChange,
    pdfFileName,
    onFileNameChange,
    defaultFileName,
    isV2Schema,
}) => {
    const { t } = useTranslation();

    // Define all available sections in order
    const sectionOptions = [
        {
            key: 'introductionPage',
            label: t('supplier-info.introduction-page'),
        },
        {
            key: 'supplements',
            label: t('supplier-info.supplements.title'),
        },
    ];

    // Add dynamic sections from ticketSections configuration
    const ticketSectionOptions = ticketSections.map(section => ({
        key: section.dataKey,
        label: section.title(),
    }));

    // Combine all sections
    sectionOptions.push(...ticketSectionOptions);
    sectionOptions.push({
        key: 'paymentHistory',
        label: t('supplier-info.payment-history'),
    });
    sectionOptions.push({
        key: 'invoiceOverview',
        label: t('supplier-info.invoice-overview'),
    });

    const allSelected = Object.values(selectedSections).every(Boolean);

    return (
        <div className="container-fluid">
            <div className="row">
                {/* Left Column - Letterhead Upload and Configuration */}
                <div className="col-md-6">
                    <h6 className="mb-3">{t('supplier-info.select-sections-for-pdf')}</h6>

                    <div className="mb-3">
                        <CheckboxAtom
                            id="selectAll"
                            label={allSelected ? t('common.deselect-all') : t('common.select-all')}
                            checked={allSelected}
                            onChange={onSelectAllSections}
                            className="fw-bold"
                        />
                    </div>

                    <div className="border rounded p-3 bg-light psm-scrollable">
                        {sectionOptions.map((option) => (
                            <div key={option.key} className="mb-2">
                                <CheckboxAtom
                                    id={option.key}
                                    label={option.label}
                                    checked={selectedSections[option.key]}
                                    onChange={() => onSectionToggle(option.key)}
                                />

                                {/* Invoice Overview Sub-options */}
                                {option.key === 'invoiceOverview' && selectedSections[option.key] && (
                                    <div className="ms-4 mt-2 p-2 border rounded bg-white">
                                        <small className="text-muted d-block mb-2">
                                            {t('supplier-info.invoice-overview-filter')}:
                                        </small>
                                        <div className="form-check mb-1">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="invoiceOverviewFilter"
                                                id="invoiceOverviewAll"
                                                value="all"
                                                checked={invoiceOverviewFilter === 'all'}
                                                onChange={(e) => onInvoiceOverviewFilterChange(e.target.value)}
                                            />
                                            <label className="form-check-label" htmlFor="invoiceOverviewAll">
                                                {t('supplier-info.invoice-overview-all')}
                                            </label>
                                        </div>
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="invoiceOverviewFilter"
                                                id="invoiceOverviewDeviations"
                                                value="deviations"
                                                checked={invoiceOverviewFilter === 'deviations'}
                                                onChange={(e) => onInvoiceOverviewFilterChange(e.target.value)}
                                            />
                                            <label className="form-check-label" htmlFor="invoiceOverviewDeviations">
                                                {t('supplier-info.invoice-overview-deviations')}
                                            </label>
                                        </div>
                                        {isV2Schema && (
                                            <div className="form-check mt-1">
                                                <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    name="invoiceOverviewFilter"
                                                    id="invoiceOverviewExceeds100"
                                                    value="exceeds100"
                                                    checked={invoiceOverviewFilter === 'exceeds100'}
                                                    onChange={(e) => onInvoiceOverviewFilterChange(e.target.value)}
                                                />
                                                <label className="form-check-label" htmlFor="invoiceOverviewExceeds100">
                                                    {t('supplier-info.invoice-overview-exceeds100')}
                                                </label>
                                            </div>
                                        )}
                                        <div className="form-check mt-2 pt-2 border-top">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="includeComments"
                                                checked={includeComments}
                                                onChange={(e) => onIncludeCommentsChange(e.target.checked)}
                                            />
                                            <label className="form-check-label" htmlFor="includeComments">
                                                {t('supplier-info.invoice-overview-include-comments')}
                                            </label>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right Column - Section Selection */}
                <div className="col-md-6">
                    <div className="ps-md-3">

                        {/* PDF Filename Field */}
                        <div className="mb-4">
                            <TextFieldAtom
                                label={t('supplier-info.pdf-filename')}
                                placeholder={t('supplier-info.pdf-filename-placeholder')}
                                value={pdfFileName}
                                onChange={(e) => onFileNameChange(e.target.value)}
                                className="text-end"
                                suffix=".pdf"
                                hint={t('supplier-info.pdf-filename-hint', { defaultName: defaultFileName })}
                            />
                        </div>

                        {/* Language Selection */}
                        <div className="mt-4">
                            <h6 className="mb-3">{t('supplier-info.pdf-language')}</h6>
                            <div className="border rounded p-3 bg-light">
                                <div className="row">
                                    <div className="col-6">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="pdfLanguage"
                                                id="languageDE"
                                                value="de"
                                                checked={selectedLanguage === 'de'}
                                                onChange={(e) => onLanguageChange(e.target.value)}
                                            />
                                            <label className="form-check-label" htmlFor="languageDE">
                                                🇩🇪 Deutsch
                                            </label>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="pdfLanguage"
                                                id="languageEN"
                                                value="en"
                                                checked={selectedLanguage === 'en'}
                                                onChange={(e) => onLanguageChange(e.target.value)}
                                            />
                                            <label className="form-check-label" htmlFor="languageEN">
                                                🇬🇧 English
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PdfSectionModal;
